import { z } from 'zod'
import { type CloseableUpsertKeys, resolveCloseableUpsertKeys } from './resolve-closeable-upsert-keys'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()

/**
 * Our internal input DTO for a Closeable save-record send (not Closeable's wire
 * schema — `CloseableService` maps this to their `{ model, values }` Contact
 * shape).
 *
 * The schema is strict on purpose: the strict parse is the guard on what reaches
 * Closeable. The numeric `lead_score` and `bad_contact` are allowed (NWA-4243);
 * segment / Score Tier / low-confidence / model-version fields stay out.
 * Advisor-relevant Lead fields and Salesforce IDs ride `metadata[]` at send time;
 * native Contact keys stay top-level.
 */
export const closeableRecordSchema = z
  .strictObject({
    external_client_id: z.string().min(1).nullable().optional(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),

    // Qualification context
    investable_assets_range: z.string().nullable(),
    annual_income: z.number().nullable(),
    age: z.number().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),

    salesforce_lead_id: z.string(),
    salesforce_opportunity_id: nullableString,
    city: nullableString,
    street: nullableString,
    assets: nullableNumber,
    cash: nullableNumber,
    current_retirement: nullableNumber,
    former_retirement: nullableNumber,
    investments: nullableNumber,
    stocks: nullableNumber,
    marital_status: nullableString,
    employment_status: nullableString,
    has_financial_advisor: nullableString,
    planning_to_move_on_retirement: nullableString,
    financial_focus: nullableString,
    retirement_scenario: nullableString,
    investor_comfort_level: nullableString,
    description: nullableString,
    lead_source: nullableString,
    utm_source: nullableString,
    utm_medium: nullableString,
    utm_campaign: nullableString,
    utm_term: nullableString,
    utm_content: nullableString,
    lead_magnet_slug: nullableString,
    lead_score: z.number().int().min(1).max(10).nullable().optional(),
    bad_contact: z.boolean().nullable().optional(),
  })
  .superRefine((record, ctx) => {
    const hasIdentity = resolveCloseableUpsertKeys({
      externalId: record.external_client_id,
      phone: record.phone,
      email: record.email,
    })
    if (!hasIdentity) {
      ctx.addIssue({
        code: 'custom',
        message: 'Closeable record requires External Id, phone, or email for upsert identity',
      })
    }
  })

export type CloseableRecord = z.infer<typeof closeableRecordSchema>

export type CloseableServiceConfig = {
  baseUrl: string
  apiToken: string
  clientProduct: string
}

/**
 * Input for `CloseableService.updateStatus`. Top-level `client_status` plus
 * optional Contact metadata (SF ids, meeting datetime, loss reason, Opportunity
 * owner, first meeting date). Null/absent/blank SF ids, meeting datetime, owner
 * fields and first meeting date are omitted (do not clear prior metadata). For
 * `lossReason`: `null`/`""` → Closeable `loss_reason: null` (clear); `undefined`
 * (Opportunity field absent) → omit the key.
 */
export type UpdateStatusParams = {
  upsertKeys: CloseableUpsertKeys
  clientStatus: string
  opportunityId?: string | null
  contactId?: string | null
  meetingScheduledAt?: string | null
  lossReason?: string | null
  ownerId?: string | null
  ownerName?: string | null
  ownerEmail?: string | null
  firstMeetingDate?: string | null
}

/**
 * Input for `CloseableService.updateLeadSignals`. `leadScore`: number → string,
 * `null` → clear, `undefined` → omit. `badContact`: `true` → `"true"`,
 * `false`/`null` → `"false"` (never leave a stale `"true"`), `undefined` → omit.
 */
export type UpdateLeadSignalsParams = {
  upsertKeys: CloseableUpsertKeys
  leadScore?: number | null
  badContact?: boolean | null
}

/** Salesforce Lead fields that drive `CloseableService.updateLeadSignals`. */
export const CLOSEABLE_LEAD_SIGNAL_SF_FIELDS = ['Lead_Score__c', 'Bad_Contact__c'] as const

/** Salesforce Opportunity fields that drive `CloseableService.updateStatus`. */
export const CLOSEABLE_STATUS_SF_FIELDS = [
  'StageName',
  'Loss_Reason__c',
  'Meeting_Date__c',
  'First_Meeting_Date__c',
  'OwnerId',
  'Sent_To_Closeable_At__c',
] as const

export type CloseableAuthorizingChangeEvent = {
  changeType: 'CREATE' | 'UPDATE' | 'DELETE'
  changedFields: string[]
}

/** CREATE always authorizes; UPDATE requires overlap with `fields`. */
export function hasAuthorizingChange(event: CloseableAuthorizingChangeEvent, fields: readonly string[]): boolean {
  if (event.changeType === 'CREATE') {
    return true
  }
  if (event.changeType !== 'UPDATE') {
    return false
  }
  return fields.some((field) => event.changedFields.includes(field))
}
